/**
 * In-App Purchase (IAP) Service
 *
 * Wraps webtoapp.design's native IAP bridge functions:
 *   - makeInAppPurchase(productId, consumable, userIdentifier)
 *   - restorePurchases()
 *
 * These are injected by the native wrapper after page load.
 * On the regular web, these functions don't exist.
 *
 * Product IDs must match App Store Connect / Google Play Console exactly.
 */

import { isNativeApp, isIOS } from '../utils/platform';

const DEV = import.meta.env.DEV;

/** Map tier keys to App Store / Google Play product IDs */
export const IAP_PRODUCTS = {
  enthusiast: 'enthusiast_monthly',
  collector:  'collector_monthly',
  showroom:   'showroom_monthly',
  team:       'team_monthly',
};

/** Reverse map: product ID → tier key */
const PRODUCT_TO_TIER = Object.fromEntries(
  Object.entries(IAP_PRODUCTS).map(([tier, productId]) => [productId, tier])
);

/**
 * Check whether IAP is available in the current environment.
 * True only inside the webtoapp.design native wrapper when the
 * bridge function has been injected.
 */
export function isIAPAvailable() {
  return isIOS && typeof window.makeInAppPurchase === 'function';
}

/**
 * Purchase a subscription tier via native in-app purchase.
 *
 * Triggers the native Apple/Google purchase sheet. The purchase flow:
 *   1. User confirms in native sheet (Face ID / Touch ID)
 *   2. webtoapp.design POSTs receipt to our iap-verify Edge Function
 *   3. Edge Function validates and updates Supabase users table
 *   4. Promise resolves with status
 *
 * @param {string} tierKey - One of: 'enthusiast', 'collector', 'showroom', 'team'
 * @param {string} userId  - Supabase user ID (forwarded as userIdentifier)
 * @returns {Promise<{status: 'completed'|'canceled'|'failed', tierKey: string}>}
 */
export async function purchaseSubscription(tierKey, userId) {
  const productId = IAP_PRODUCTS[tierKey];
  if (!productId) {
    throw new Error(`Unknown tier: ${tierKey}`);
  }

  if (!isIAPAvailable()) {
    throw new Error('In-app purchases are not available in this environment');
  }

  if (DEV) console.log('[IAP] Starting purchase:', { tierKey, productId, userId });

  try {
    // consumable = false for subscriptions
    // Add timeout — if the native bridge never resolves (user dismisses sheet
    // in certain edge cases), we don't want to hang forever.
    const PURCHASE_TIMEOUT = 10000; // 10 seconds
    const result = await Promise.race([
      window.makeInAppPurchase(productId, false, userId),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Purchase timed out')), PURCHASE_TIMEOUT)
      ),
    ]);

    if (DEV) console.log('[IAP] Purchase result:', JSON.stringify(result));

    const status = result?.status || 'failed';
    const errorMessage = result?.error || result?.errorMessage || null;
    return { status, tierKey, errorMessage };
  } catch (err) {
    if (DEV) console.error('[IAP] Purchase error:', err);
    const message = err?.message || err?.toString() || 'Unknown purchase error';
    return { status: 'failed', tierKey, errorMessage: message };
  }
}

/**
 * Restore previous purchases (required by Apple for subscriptions).
 *
 * Useful when a user reinstalls the app or gets a new device.
 * webtoapp.design handles the native restore flow and calls the
 * verification endpoint for each restored purchase.
 *
 * @returns {Promise<{status: 'completed'|'failed'}>}
 */
export async function restoreIAPPurchases() {
  if (!isNativeApp || typeof window.restorePurchases !== 'function') {
    throw new Error('Restore purchases is not available in this environment');
  }

  if (DEV) console.log('[IAP] Restoring purchases...');

  try {
    const result = await window.restorePurchases();
    if (DEV) console.log('[IAP] Restore result:', result);
    return { status: result?.status || 'completed' };
  } catch (err) {
    if (DEV) console.error('[IAP] Restore error:', err);
    return { status: 'failed' };
  }
}

/**
 * Get the tier key from a product ID.
 * @param {string} productId - e.g. 'enthusiast_monthly'
 * @returns {string|null} - e.g. 'enthusiast' or null
 */
export function getTierFromProductId(productId) {
  return PRODUCT_TO_TIER[productId] || null;
}
